<script setup>
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import { useUserStore } from "@/entities";
import { IconAngleLeft, isObject, isNil, isArray } from "@/shared";

import { useMenuAccess } from "../lib/useMenuAccess";
import { useSidebarTooltips } from "../lib/useSidebarTooltips";
import { useSidebarStore } from "../model/useSidebarStore";

// config
const props = defineProps({
    // Ключ пункта в конфигурации меню: по нему считаем права и помним,
    // раскрыта ли группа
    linkKey: String,
    linkMeta: Object,
    hasAccess: {
        type: Boolean,
        default: false,
    },
    isCollapsed: Boolean,
});

const emit = defineEmits(["toggleChildren"]);

const route = useRoute();

const sidebarStore = useSidebarStore();
const userStore = useUserStore();
const { checkLinkAccess } = useMenuAccess();

// validate
const shouldDisplayLink = computed(() => {
    return (
        props.linkMeta &&
        (props.hasAccess ||
            (userStore.isDirector && props.linkMeta.shouldDisplayWithoutAccess))
    );
});

// active
const isActive = computed(() => {
    if (isNil(props.linkMeta.activeRoutes)) return false;

    if (isArray(props.linkMeta.activeRoutes)) {
        const allLinks = props.linkMeta.activeRoutes.reduce((links, link) => {
            links.push(...Object.values(link));

            return links;
        }, []);

        return allLinks.some((routeName) => routeName === route.name);
    }

    return Object.values(props.linkMeta.activeRoutes).some(
        (routeName) => routeName === route.name
    );
});

// children
const hasChildren = computed(() => {
    return (
        isObject(props.linkMeta.children) &&
        Object.entries(props.linkMeta.children).length > 0
    );
});

// Пока пользователь сам не свернул группу, она раскрыта, если внутри активный
// маршрут или так задано в конфигурации — иначе подпункт не найти
const childrenOpened = computed(() => {
    const storedState = sidebarStore.isSectionOpened(props.linkKey);

    if (isNil(storedState)) {
        return isActive.value || Boolean(props.linkMeta.defaultOpened);
    }

    return storedState;
});

// Ушли в раздел из свёрнутой группы — раскрываем её
watch(isActive, (active) => {
    if (!active || !hasChildren.value || childrenOpened.value) return;

    sidebarStore.setSectionOpened(props.linkKey, true);
    emit("toggleChildren");
});

const toggleChildren = () => {
    if (!props.hasAccess) return;

    sidebarStore.setSectionOpened(props.linkKey, !childrenOpened.value);
    emit("toggleChildren");
};

// tooltips
const linkRef = ref(null);

const { t } = useI18n();

// Заблокированный Pro-раздел — показываем подсказку с текстом и ссылкой на смену
// тарифа (VNode из конфигурации). Иначе — название раздела для свёрнутого меню.
// label — готовая подпись пункта, title — ключ перевода с сервера.
const tooltipContent =
    !props.hasAccess && props.linkMeta?.tooltipContent
        ? props.linkMeta.tooltipContent
        : props.linkMeta?.label ?? t(props.linkMeta?.title ?? "");

const isTooltipInteractive =
    !props.hasAccess && props.linkMeta.shouldDisplayWithoutAccess;

const shouldHideTooltip = computed(() => {
    return (
        !props.isCollapsed &&
        (props.hasAccess || !props.linkMeta.shouldDisplayWithoutAccess)
    );
});

useSidebarTooltips({
    tooltipContent,
    isTooltipInteractive,
    shouldHideTooltip,
    element: linkRef,
});

const handleNavigate = (e, navigate) => {
    const isMobile = window.innerWidth <= 576;

    if (isMobile) {
        sidebarStore.setCollapsedMode(true);
    }

    navigate(e);
};
</script>

<template>
    <!-- Заголовок группы: своей страницы у неё нет, клик раскрывает список -->
    <button
        v-if="shouldDisplayLink && hasChildren"
        type="button"
        class="sidebar-link sidebar-link--group"
        :class="{
            'sidebar-link--collapsed': isCollapsed,
            'sidebar-link--group-current': isActive,
        }"
        :aria-expanded="childrenOpened"
        @click="toggleChildren"
        v-bind="$attrs"
        ref="linkRef"
    >
        <component
            class="sidebar-link__icon"
            v-if="linkMeta.icon"
            :is="linkMeta.icon"
            aria-hidden="true"
        />
        {{ linkMeta.label ?? $t(linkMeta.title) }}
        <IconAngleLeft
            class="sidebar-link__angle"
            :class="{ 'sidebar-link__angle--rotate-down': childrenOpened }"
            aria-hidden="true"
        />
    </button>
    <!-- Раздел вне тарифа: не ссылка, вместо перехода показываем подсказку -->
    <div
        v-else-if="shouldDisplayLink && !hasAccess"
        class="sidebar-link sidebar-link--disabled"
        :class="{
            'sidebar-link--collapsed': isCollapsed,
            'sidebar-link--active': isActive,
        }"
        v-bind="$attrs"
        ref="linkRef"
    >
        <component
            class="sidebar-link__icon"
            v-if="linkMeta.icon"
            :is="linkMeta.icon"
            aria-hidden="true"
        />
        {{ linkMeta.label ?? $t(linkMeta.title) }}
    </div>
    <RouterLink
        v-else-if="shouldDisplayLink"
        :to="linkMeta.to ?? {}"
        custom
        v-slot="{ href, navigate }"
    >
        <a
            class="sidebar-link"
            :class="{
                'sidebar-link--collapsed': isCollapsed,
                'sidebar-link--active': isActive,
            }"
            :href="href"
            :aria-current="isActive ? 'page' : undefined"
            @click="handleNavigate($event, navigate)"
            v-bind="$attrs"
            ref="linkRef"
        >
            <component
                class="sidebar-link__icon"
                v-if="linkMeta.icon"
                :is="linkMeta.icon"
                aria-hidden="true"
            />
            {{ linkMeta.label ?? $t(linkMeta.title) }}
        </a>
    </RouterLink>

    <div class="sidebar-link__children" v-if="hasChildren && childrenOpened">
        <SidebarLink
            v-for="(childLink, key) in linkMeta.children"
            :key="key"
            :link-key="key"
            :link-meta="childLink"
            :is-collapsed="isCollapsed"
            :has-access="checkLinkAccess(key, childLink)"
        />
    </div>
</template>

<style scoped lang="scss">
$sidebar-link: ".sidebar-link";

#{$sidebar-link} {
    display: flex;

    align-items: center;

    padding: 14px;

    font-size: 13px;
    font-weight: 500;

    color: var(--c-neutral-30);
    text-decoration: none;
    white-space: nowrap;

    border-left: 0 solid var(--c-primary);

    transition: all var(--sidebar-collapse-transition-duration);

    &:hover {
        color: var(--c-neutral-10);

        cursor: pointer;

        background-color: var(--sidebar-background-hover);
    }

    &__icon {
        flex-shrink: 0;

        width: var(--sidebar-link-icon-width);
        height: var(--sidebar-link-icon-height);
        margin-right: 8px;

        transition: margin-right var(--sidebar-collapse-transition-duration);
    }

    &__angle {
        width: auto;
        height: 14px;
        margin-left: auto;

        transition: transform 0.2s;

        &--rotate-down {
            transform: rotateZ(-90deg);
        }
    }

    &__children {
        background-color: var(--sidebar-background-hover);

        transition: all var(--sidebar-collapse-transition-duration);

        // Пункты группы — с отступом под заголовок и плотнее корневых
        #{$sidebar-link} {
            padding: 9px 9px 9px 29px;
        }
    }

    &--collapsed {
        @media (min-width: 577px) {
            padding: 14px
                calc(
                    (
                            var(--sidebar-collapsed-width-const) -
                                var(--sidebar-link-icon-width)
                        ) / 2
                );

            #{$sidebar-link}__icon {
                margin-right: 40px;
            }
        }
    }

    // Заголовок группы — подпись над списком, а не ещё одна ссылка:
    // мельче, капсом и без заливки на наведении
    &--group {
        padding: 16px 14px 6px;

        font-size: 11px;
        font-weight: 600;
        text-align: left;
        text-transform: uppercase;

        letter-spacing: 0.04em;

        background-color: transparent;

        &:hover {
            background-color: transparent;
        }

        #{$sidebar-link}__angle {
            height: 10px;
        }
    }

    // Внутри группы открытый раздел — подсвечиваем и её заголовок
    &--group-current {
        color: var(--c-neutral-10);
    }

    &--disabled {
        cursor: help !important;

        background-color: var(--sidebar-background-disabled) !important;

        // opacity — универсальный способ показать "недоступно" на любом фоне
        opacity: 0.4;

        &:hover {
            opacity: 0.55;
        }
    }

    &--active {
        color: var(--c-neutral-10);

        // Тиловый оттенок — визуально отличается от hover (серый)
        background-color: rgb(20 184 166 / 10%);
        border-left-width: 4px;
    }
}
</style>
